import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import { AgxHeader, Breakpoints, Images, useWindowSize } from '@urbanx/agx-ui-components';
import { AgxButton, AgxSpacer, AgxTypography } from '@urbanx/agx.ui-components2';

import useLogout from 'hooks/useLogout';
import './SettingsLayout.scss';

const settingsLinks = [
  {
    label: 'Profile',
    to: '/settings/profile',
  },
  {
    label: 'Integrations',
    to: '/settings/integrations',
  },
] as const;

const SettingsLayout = () => {
  const logout = useLogout();
  const location = useLocation();
  const breakpoint = useWindowSize();
  const isMobile = breakpoint === Breakpoints.Mobile;
  const isCompact = isMobile || breakpoint === Breakpoints.Tablet_Portrait;
  const activeLink = settingsLinks.find(({ to }) => to === location.pathname);

  if (!activeLink) {
    return <Navigate to={settingsLinks[0].to} replace />;
  }

  return (
    <AgxSpacer className="settings-layout" gap="spacing-3xl">
      <AgxSpacer padding={isMobile ? { left: 'spacing-2xl', right: 'spacing-2xl' } : undefined}>
        <AgxHeader size={1} inverse>
          Settings
        </AgxHeader>
      </AgxSpacer>

      <AgxSpacer
        className={`settings-layout__panel${isCompact ? ' settings-layout__panel--compact' : ''}`}
        direction={isCompact ? 'vertical' : 'horizontal'}
        gap={isCompact ? 'spacing-md' : 'spacing-3xl'}
        padding={isCompact ? 'spacing-2xl' : 'spacing-3xl'}
        hideOverflow
      >
        {isCompact ? (
          <nav className="settings-layout__mobile-nav" aria-label="Settings">
            <Link
              className="settings-layout__nav-link settings-layout__nav-link--active"
              to={activeLink.to}
              aria-current="page"
            >
              <AgxTypography
                id="settings-mobile-current-page"
                type="body_ui_strong"
                size="medium"
                color="inherit"
              >
                {activeLink.label}
              </AgxTypography>
            </Link>
          </nav>
        ) : (
          <AgxSpacer
            className="settings-layout__navigation"
            contentJustification="space-between"
            flexShrink={0}
          >
            <nav className="settings-layout__nav" aria-label="Settings">
              <AgxSpacer>
                {settingsLinks.map(({ label, to }) => (
                  <Link
                    className={`settings-layout__nav-link${
                      activeLink.to === to ? ' settings-layout__nav-link--active' : ''
                    }`}
                    key={to}
                    to={to}
                    aria-current={activeLink.to === to ? 'page' : undefined}
                  >
                    <AgxTypography
                      id={`settings-${label.toLowerCase()}-link-label`}
                      type={activeLink.to === to ? 'body_ui_strong' : 'body_ui'}
                      size="medium"
                      color="inherit"
                    >
                      {label}
                    </AgxTypography>
                  </Link>
                ))}
              </AgxSpacer>
            </nav>

            <AgxButton
              id="settings-logout"
              className="settings-layout__logout"
              variant="image"
              size="large"
              text={
                <AgxTypography
                  id="settings-logout-label"
                  type="body_ui"
                  size="medium"
                  color="inherit"
                >
                  Logout
                </AgxTypography>
              }
              rightIcon={<Images.Logout aria-hidden="true" />}
              onClick={logout}
            />
          </AgxSpacer>
        )}

        <AgxSpacer
          className="settings-layout__content"
          data-testid="settings-content"
          padding={isCompact ? 'spacing-2xl' : 'spacing-4xl'}
        >
          <Outlet />
        </AgxSpacer>
      </AgxSpacer>
    </AgxSpacer>
  );
};

export default SettingsLayout;
